'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'

const AVAILABLE_TAGS = [
  'Web Development', 'React', 'Next.js', 'Python', 
  'Linux', 'VPS Hosting', 'Cybersecurity', 'OSINT', 
  'Kotlin', 'Docker', 'Game Development', 'APIs'
]

export default function WelcomeOnboarding({ isPending }: { isPending: boolean }) {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState(1)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  useEffect(() => {
    if (isPending) {
      setIsOpen(true)
      // Lock scrolling on the body while the modal is open
      document.body.style.overflow = 'hidden'
    }
    return () => { document.body.style.overflow = 'unset' }
  }, [isPending])

  if (!isOpen) return null

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    )
  }

  const handleComplete = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/user/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interests: selectedTags })
      })

      if (res.ok) {
        setIsOpen(false)
        document.body.style.overflow = 'unset'
        router.refresh()
      }
    } catch (error) {
      console.error('Failed to save onboarding preferences')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header Graphic */}
        <div className="h-32 bg-gradient-to-br from-indigo-600 via-indigo-800 to-slate-900 relative flex items-center justify-center">
          <div className="absolute inset-0 bg-[url('/noise.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
          <h2 className="text-3xl font-black text-white relative z-10 tracking-tight">
            {step === 1 ? 'Welcome to DevYield 🚀' : 'Tailor Your Feed 🎯'}
          </h2>
        </div>

        {/* Content Body */}
        <div className="p-8 sm:p-10 overflow-y-auto hide-scrollbar flex-1">
          {step === 1 ? (
            <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-500">
              <p className="text-slate-300 text-center text-lg font-medium leading-relaxed">
                You are entering a developer community where your knowledge actually pays off. Here is how the ecosystem works:
              </p>
              
              <div className="grid grid-cols-1 gap-4 mt-6">
                <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl flex gap-4 items-center">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center text-2xl shrink-0">💬</div>
                  <div>
                    <h4 className="text-white font-bold">1. Contribute & Connect</h4>
                    <p className="text-sm text-slate-400 mt-1">Ask questions, share code snippets, and solve problems for other developers.</p>
                  </div>
                </div>
                
                <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl flex gap-4 items-center">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center text-2xl shrink-0">💰</div>
                  <div>
                    <h4 className="text-white font-bold">2. Earn DevYield Points</h4>
                    <p className="text-sm text-slate-400 mt-1">Every helpful reply, daily login, and upvote earns you currency.</p>
                  </div>
                </div>

                <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl flex gap-4 items-center">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center text-2xl shrink-0">🌐</div>
                  <div>
                    <h4 className="text-white font-bold">3. Claim Servers & Domains</h4>
                    <p className="text-sm text-slate-400 mt-1">Head to the Points Store to automatically provision your rewards.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-500">
              <p className="text-slate-300 text-center text-lg font-medium">
                What are you currently working on? Select a few topics so we can personalize your dashboard.
              </p>
              
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                {AVAILABLE_TAGS.map(tag => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-5 py-3 rounded-xl font-bold text-sm transition-all border ${
                      selectedTags.includes(tag)
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-600/25'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-slate-950 border-t border-slate-800 flex justify-between items-center shrink-0">
          <div className="flex gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${step === 1 ? 'bg-indigo-500' : 'bg-slate-700'}`}></div>
            <div className={`w-2.5 h-2.5 rounded-full ${step === 2 ? 'bg-indigo-500' : 'bg-slate-700'}`}></div>
          </div>
          
          {step === 1 ? (
            <button 
              onClick={() => setStep(2)}
              className="bg-white text-slate-900 hover:bg-slate-200 font-bold py-3 px-8 rounded-xl transition-colors shadow-lg"
            >
              Next Step ➔
            </button>
          ) : (
            <button 
              onClick={handleComplete}
              disabled={loading || selectedTags.length === 0}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-8 rounded-xl transition-all shadow-lg shadow-indigo-600/25 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? 'Setting up...' : 'Enter DevYield'}
            </button>
          )}
        </div>

      </div>
    </div>
  )
}